/**
 * @file src/_modulos/prenota/tabela/layouts/DataTable.tsx
 * @description Componente simplificado de tabela de dados
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas renderiza tabela
 * - YAGNI: Remove props não utilizadas
 */

"use client";

import { DataTableProps, useTableStore, Footer } from "@PN/tabela";
import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
  Loader,
} from "visuais";
import { motion, AnimatePresence } from "motion/react";

const TableContext = React.createContext<any>(null);

export const useReactTableContext = () => {
  const context = React.useContext(TableContext);
  if (!context) {
    console.warn("useReactTableContext must be inside <DataTable>");
  }
  return context;
};

/**
 * Componente de tabela de dados
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  enableSorting = true,
  showFooter = true,
  footerProps,
  hasAttemptedSearch = false,
}: DataTableProps<TData, TValue> & {
  showFooter?: boolean;
  footerProps?: {
    data?: any;
    filials?: string[];
    tab?: string;
  };
  hasAttemptedSearch?: boolean;
}) {
  const { sorting, setSorting } = useTableStore();

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    manualSorting: true,
  });

  return (
    <TableContext.Provider value={table}>
      <div className="overflow-hidden rounded-lg border min-w-0 h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <Table className="table-fixed min-w-full">
            <TableHeader className="bg-muted">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header.getSize()
                          ? `${header.getSize()}px`
                          : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <motion.tr
                    key="loading"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <TableCell
                      colSpan={columns.length}
                      className="h-[600px] p-0"
                    >
                      <motion.div
                        className="w-full h-full flex items-center justify-center"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      >
                        <Loader size={120} />
                      </motion.div>
                    </TableCell>
                  </motion.tr>
                ) : table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row, index) => {
                    const original: any = row.original as any;
                    const tipo = String(original?.F1_XTIPO ?? "")
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                      .toLowerCase()
                      .trim()
                      .replace(/\s+/g, " ");
                    const hasAttachment = Boolean(String(original?.Z07_CHAVE || "").trim());
                    const isRevendaOuMateriaPrima = tipo === "revenda" || tipo === "materia prima";
                    const highlight = hasAttachment && isRevendaOuMateriaPrima;
                    return (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.3,
                          ease: "easeOut",
                          delay: index * 0.05, // Stagger effect
                        }}
                        data-state={row.getIsSelected() && "selected"}
                        className={`relative ${highlight ? "bg-neutral-300/60 hover:bg-neutral-300 dark:bg-neutral-800 dark:hover:bg-neutral-700/60" 
                          : "hover:bg-muted/50"}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            style={{ width: `${cell.column.getSize()}px` }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </motion.tr>
                    );
                  })
                ) : hasAttemptedSearch ? (
                  <motion.tr
                    key="no-results"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        Sem resultados.
                      </motion.div>
                    </TableCell>
                  </motion.tr>
                ) : (
                  <motion.tr
                    key="initial-loading"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <TableCell
                      colSpan={columns.length}
                      className="h-[600px] p-0"
                    >
                      <motion.div
                        className="w-full h-full flex items-center justify-center"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      >
                        <Loader size={120} />
                      </motion.div>
                    </TableCell>
                  </motion.tr>
                )}
              </AnimatePresence>
            </TableBody>

            {showFooter && (
              <TableFooter className="bg-muted sticky bottom-0">
                <TableRow>
                  <TableCell colSpan={columns.length} className="p-0">
                    <Footer {...footerProps} />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      </div>
    </TableContext.Provider>
  );
}
